'use client'

import { useTranslations } from 'next-intl'
import { Component, ReactNode } from 'react'

import { ErrorPageContent } from '../../features/error/ErrorPageContent'
import { showNotification } from '../Notification'

interface Props {
	children: ReactNode
	errorTitle?: string
	errorMessage?: string
}

interface State {
	hasError: boolean
}

class ErrorBoundaryCore extends Component<Props, State> {
	constructor(props: Props) {
		super(props)
		this.state = { hasError: false }
	}

	static getDerivedStateFromError(): State {
		return { hasError: true }
	}

	componentDidCatch(error: Error) {
		console.error('ErrorBoundary caught an error:', error)

		const errorMessage =
			this.props.errorMessage ||
			'An unexpected error occurred. Please try again.'
		showNotification(errorMessage, {
			type: 'error',
		})
	}

	render() {
		if (this.state.hasError) {
			return (
				<ErrorPageContent
					errorCode="500"
					customTitle={this.props.errorTitle}
					customDescription={this.props.errorMessage}
					hideQrSection={true}
				/>
			)
		}

		return this.props.children
	}
}

export const ErrorBoundary = ({ children }: { children: ReactNode }) => {
	const t = useTranslations()

	return (
		<ErrorBoundaryCore
			errorTitle={t('public__generic__unexpected_error_title')}
			errorMessage={t('public__generic__unexpected_error_subtitle')}
		>
			{children}
		</ErrorBoundaryCore>
	)
}
